package com.r_estrategica.redacaoestrategica.ui

import androidx.compose.animation.animateContentSize
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.rounded.Psychology
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.r_estrategica.redacaoestrategica.data.*

// --- 1. MODELO DE DADOS TÁTICO ---
data class DicaSubstituicao(
    val pergunta: String,
    val subcategoria: String,
    val sinonimos: List<String>
)

// Dicas especiais focadas nas maiores dores do ENEM
object BancoSubstituicoesTaticas {
    val lista = listOf(
        DicaSubstituicao(
            pergunta = "Já usei a palavra SOCIEDADE demais!",
            subcategoria = "Cidadãos / Coletivo",
            sinonimos = listOf("corpo social", "tecido social", "coletividade", "contingente populacional", "cidadãos", "população", "comunidade")
        ),
        DicaSubstituicao(
            pergunta = "Já usei a palavra GOVERNO demais!",
            subcategoria = "Estado / Autoridades",
            sinonimos = listOf("Estado", "poder público", "máquina estatal", "esfera governamental", "instituição", "autoridades competentes")
        ),
        DicaSubstituicao(
            pergunta = "Já usei a palavra CAUSAR demais!",
            subcategoria = "Verbos de consequência",
            sinonimos = listOf("desencadear", "culminar", "acarretar", "fomentar", "instigar", "propiciar", "provocar", "gerar")
        ),
        DicaSubstituicao(
            pergunta = "Já usei a palavra PROBLEMA demais!",
            subcategoria = "Dificuldades / Impasses",
            sinonimos = listOf("impasse", "revés", "chaga social", "obstáculo", "problemática", "desafio", "infortúnio", "imbróglio")
        ),
        DicaSubstituicao(
            pergunta = "Como retomar o Argumento 1 e 2 na Conclusão?",
            subcategoria = "Conectivos de Retomada",
            sinonimos = listOf("Diante dos impasses supracitados...", "Sob essa ótica...", "Nesse viés...", "A partir desse contexto...", "Diante desse cenário...")
        ),
        DicaSubstituicao(
            pergunta = "Já usei a palavra PESSOAS demais!",
            subcategoria = "Indivíduos",
            sinonimos = listOf("indivíduos", "sujeitos", "cidadãos", "seres humanos", "parcela da população")
        ),
        DicaSubstituicao(
            pergunta = "Já usei a palavra IMPORTANTE demais!",
            subcategoria = "Relevância / Necessidade",
            sinonimos = listOf("imprescindível", "crucial", "fundamental", "relevante", "indispensável", "vital", "urgente")
        ),
        DicaSubstituicao(
            pergunta = "Já usei a palavra HOJE demais!",
            subcategoria = "Tempo / Atualidade",
            sinonimos = listOf("atualmente", "hodiernamente", "no cenário contemporâneo", "na atualidade", "no século XXI")
        ),
        DicaSubstituicao(
            pergunta = "Já usei a palavra BRASIL demais!",
            subcategoria = "Nação",
            sinonimos = listOf("nação tupiniquim", "território nacional", "país", "Estado brasileiro", "nação verde-amarela")
        ),
        DicaSubstituicao(
            pergunta = "Como substituir o verbo FAZER?",
            subcategoria = "Ações práticas",
            sinonimos = listOf("realizar", "efetuar", "promover", "executar", "implementar", "desenvolver", "articular")
        )
    )
}

// --- 2. TELA PRINCIPAL ---
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TelaSinonimos(
    aoClicarVoltar: () -> Unit,
    isPremium: Boolean
) {
    val corPrimaria = MaterialTheme.colorScheme.primary
    val corTextoContraste = MaterialTheme.colorScheme.onPrimary
    val corFundo = MaterialTheme.colorScheme.background

    var textoBusca by remember { mutableStateOf("") }
    val clipboardManager = LocalClipboardManager.current

    // BackHandler {
    //    aoClicarVoltar()
    // }

    // 🔹 FUSÃO MÁGICA DOS DOIS BANCOS DE DADOS
    val listaCompleta = remember {
        val taticas = BancoSubstituicoesTaticas.lista

        // Evita duplicar palavras que já têm uma dica tática super detalhada
        val palavrasTaticasEvitar = listOf("sociedade", "governo", "causar", "problema", "pessoas", "importante", "hoje", "brasil", "fazer")

        // Converte o seu banco original (Verbete) para o novo formato (DicaSubstituicao)
        val doBancoOriginal = BancoSinonimos.listaOriginal
            .filter { verbete -> !palavrasTaticasEvitar.contains(verbete.palavraChave.lowercase()) }
            .map { verbete ->
                DicaSubstituicao(
                    pergunta = "Como substituir '${verbete.palavraChave.uppercase()}'?",
                    subcategoria = if (verbete.categoria.isNotEmpty()) verbete.categoria else "Vocabulário Geral",
                    sinonimos = verbete.sinonimos
                )
            }
            .sortedBy { it.pergunta } // Ordena o restante em ordem alfabética para facilitar

        taticas + doBancoOriginal
    }

    // Aplica o filtro de busca em cima da lista unida
    val listaFiltrada = remember(textoBusca, listaCompleta) {
        if (textoBusca.isBlank()) {
            listaCompleta
        } else {
            listaCompleta.filter { dica ->
                dica.pergunta.contains(textoBusca, ignoreCase = true) ||
                        dica.subcategoria.contains(textoBusca, ignoreCase = true) ||
                        dica.sinonimos.any { it.contains(textoBusca, ignoreCase = true) }
            }
        }
    }

    fun copiarParaClipboard(texto: String) {
        clipboardManager.setText(AnnotatedString(texto))
        println("\"$texto\" copiado!")
    }

    Scaffold(
        topBar = {
            CenterAlignedTopAppBar(
                title = {
                    Text(
                        "Banco de Substituições",
                        fontWeight = FontWeight.Bold,
                        color = corTextoContraste
                    )
                },
                navigationIcon = {
                    IconButton(onClick = aoClicarVoltar) {
                        Icon(
                            Icons.AutoMirrored.Filled.ArrowBack,
                            null,
                            tint = corTextoContraste
                        )
                    }
                },
                colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                    containerColor = corPrimaria
                )
            )
        },
        containerColor = corFundo
    ) { padding ->
        Column(
            modifier = Modifier
                .padding(padding)
                .fillMaxSize()
        ) {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = corPrimaria,
                shadowElevation = 4.dp,
                shape = RoundedCornerShape(bottomStart = 24.dp, bottomEnd = 24.dp)
            ) {
                Column(modifier = Modifier.padding(16.dp).padding(bottom = 8.dp)) {
                    Text(
                        "Qual é a sua dor no texto agora?",
                        color = corTextoContraste.copy(alpha = 0.9f),
                        fontWeight = FontWeight.Bold,
                        fontSize = 14.sp
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    OutlinedTextField(
                        value = textoBusca,
                        onValueChange = { textoBusca = it },
                        placeholder = {
                            Text(
                                "Buscar termo repetido (ex: Problema)...",
                                color = corTextoContraste.copy(0.7f),
                                fontSize = 14.sp
                            )
                        },
                        leadingIcon = {
                            Icon(
                                Icons.Default.Search,
                                null,
                                tint = corTextoContraste
                            )
                        },
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = corTextoContraste,
                            unfocusedBorderColor = corTextoContraste.copy(0.5f),
                            cursorColor = corTextoContraste,
                            focusedTextColor = corTextoContraste,
                            unfocusedTextColor = corTextoContraste,
                            focusedContainerColor = Color.Transparent,
                            unfocusedContainerColor = Color.Transparent
                        ),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true
                    )
                }
            }

            LazyColumn(
                contentPadding = PaddingValues(
                    start = 16.dp,
                    end = 16.dp,
                    top = 16.dp,
                    bottom = 80.dp
                ),
                verticalArrangement = Arrangement.spacedBy(12.dp),
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
            ) {
                items(listaFiltrada) { dica ->
                    CardSubstituicaoTatica(
                        dica = dica,
                        corDestaque = corPrimaria,
                        onCopiar = ::copiarParaClipboard
                    )
                }
            }
        }
    }
}

// --- 3. COMPONENTE DE CARD EXPANSÍVEL TÁTICO ---
@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun CardSubstituicaoTatica(
    dica: DicaSubstituicao,
    corDestaque: Color,
    onCopiar: (String) -> Unit
) {
    var expandido by remember { mutableStateOf(false) }

    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(2.dp),
        shape = RoundedCornerShape(12.dp),
        modifier = Modifier
            .fillMaxWidth()
            .animateContentSize(animationSpec = tween(300))
            .clickable { expandido = !expandido }
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.weight(1f)
                ) {
                    Box(
                        modifier = Modifier
                            .size(40.dp)
                            .background(
                                corDestaque.copy(alpha = 0.1f),
                                RoundedCornerShape(8.dp)
                            ),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            Icons.Rounded.Psychology,
                            null,
                            tint = corDestaque,
                            modifier = Modifier.size(24.dp)
                        )
                    }

                    Spacer(Modifier.width(12.dp))

                    Column {
                        Text(
                            text = dica.pergunta,
                            fontSize = 16.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color.Black
                        )
                        if (dica.subcategoria.isNotEmpty()) {
                            Text(
                                text = dica.subcategoria,
                                fontSize = 12.sp,
                                color = corDestaque,
                                fontWeight = FontWeight.SemiBold
                            )
                        }
                    }
                }

                Icon(
                    imageVector = if (expandido) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                    contentDescription = null,
                    tint = Color.DarkGray
                )
            }

            if (expandido) {
                Spacer(Modifier.height(12.dp))
                HorizontalDivider(color = Color.LightGray.copy(0.3f))
                Spacer(Modifier.height(12.dp))

                Text(
                    text = "Opções para curar seu texto (Toque para copiar):",
                    fontSize = 12.sp,
                    color = Color.DarkGray,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(bottom = 8.dp)
                )

                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    dica.sinonimos.forEach { sinonimo ->
                        SuggestionChip(
                            onClick = { onCopiar(sinonimo) },
                            label = {
                                Text(
                                    sinonimo,
                                    color = Color.Black,
                                    fontWeight = FontWeight.Medium
                                )
                            },
                            icon = {
                                Icon(
                                    Icons.Default.ContentCopy,
                                    contentDescription = null,
                                    modifier = Modifier.size(14.dp),
                                    tint = corDestaque
                                )
                            },
                            colors = SuggestionChipDefaults.suggestionChipColors(
                                containerColor = Color(0xFFF5F7FA),
                                labelColor = Color.Black
                            ),
                            border = SuggestionChipDefaults.suggestionChipBorder(
                                enabled = true,
                                borderColor = corDestaque.copy(alpha = 0.5f)
                            )
                        )
                    }
                }
            }
        }
    }
}
